import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Button } from '../../../../components-v2/Button';
import { Input, InputAddon, InputGroup } from '../../../../components-v2/Input';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/Select';
import { Separator } from '../../../../components/Separator';
import { Toggle } from '../../../../components/Toggle';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { styleSheet } from '../styles';
import { DonationServiceFeeCalculationMethod, DonationSurveyFormData } from '../utils';

const serviceFeeCalculationMethodNames: Record<DonationServiceFeeCalculationMethod, string> = {
	[DonationServiceFeeCalculationMethod.STRIPE]: 'Stripe',
	[DonationServiceFeeCalculationMethod.FIXED]: 'Fixed',
	[DonationServiceFeeCalculationMethod.PERCENTAGE]: 'Percentage',
};

const serviceFeeCalculationOptions = Object.values(DonationServiceFeeCalculationMethod).map(method => ({
	label: serviceFeeCalculationMethodNames[method],
	value: method,
}));

export const DonationOptions: React.FC<{ survey: Api.IDonationSurvey }> = ({ survey }) => {
	const isArchived = !!survey?.archivedDate;

	const {
		control,
		watch,
		formState: { errors },
	} = useFormContext<DonationSurveyFormData>();
	const formValues = watch();

	const {
		fields: valueFields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'donationValues',
	});

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Donation Options</h3>

			<div className={css(styleSheet.formField)}>
				<div className={css(styleSheet.formFieldLabelContainer)}>
					<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
						<label htmlFor={'has-donation-values-toggle'} className={css(styleSheet.formFieldLabel)}>
							Set Donation Amount
						</label>

						<Controller
							name='hasDonationValues'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Toggle
									id='has-donation-values-toggle'
									isOn={fieldProps.value}
									onToggleCheckChanged={fieldProps.onChange}
									uncheckedColor={THEME.colors.background.dark}
								/>
							)}
						/>
					</div>

					<span className={css(styleSheet.formFieldDescription)}>
						Specify the donation amounts you would like to accept
					</span>
				</div>

				<div className={css(styleSheet.donationValuesContainer)}>
					{valueFields.map((field, index) => (
						<div key={field.id} className={css(styleSheet.donationValue)}>
							<Controller
								name={`donationValues.${index}.value`}
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<InputGroup>
										<Input
											type='text'
											disabled={isArchived}
											value={fieldProps.value ? `$${fieldProps.value}` : ''}
											onChange={e => {
												const inputValue = e.target.value;
												// Remove $ and any non-numeric characters except decimal point
												const numericValue = inputValue.replace(/[^0-9.]/g, '');
												const parsedValue = parseFloat(numericValue);

												if (numericValue === '' || numericValue === '.') {
													fieldProps.onChange(0);
												} else if (!isNaN(parsedValue)) {
													fieldProps.onChange(parsedValue);
												}
											}}
											placeholder='$0'
										/>
										<InputAddon onClick={() => remove(index)} style={{ cursor: 'pointer' }}>
											<XIcon />
										</InputAddon>
									</InputGroup>
								)}
							/>

							{errors.donationValues?.[index]?.value?.message && (
								<span className={css(styleSheet.errorMessage)}>{errors.donationValues?.[index]?.value?.message}</span>
							)}
						</div>
					))}

					<Button
						type='button'
						variant='link'
						onClick={() => append({ value: 0 })}
						disabled={isArchived || !formValues.hasDonationValues}
						styles={[styleSheet.addOptionButton]}
					>
						+ Add Option
					</Button>
				</div>
			</div>

			<Separator />

			<div className={css(styleSheet.formField)}>
				<div className={css(styleSheet.formFieldLabelContainer)}>
					<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
						<label htmlFor={'allow-custom-donation-amount-toggle'} className={css(styleSheet.formFieldLabel)}>
							Allow Custom Donation Amount
						</label>

						<Controller
							name='allowCustomDonationAmount'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Toggle
									id='allow-custom-donation-amount-toggle'
									isOn={fieldProps.value}
									onToggleCheckChanged={fieldProps.onChange}
									uncheckedColor={THEME.colors.background.dark}
								/>
							)}
						/>
					</div>

					<span className={css(styleSheet.formFieldDescription)}>
						Allow donors to specify how much they want to give
					</span>
				</div>
			</div>

			{formValues.allowCustomDonationAmount && (
				<>
					<Separator />

					<div className={css(styleSheet.formField)}>
						<div className={css(styleSheet.formFieldLabelContainer)}>
							<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
								<label htmlFor={'has-minimum-donation-amount-toggle'} className={css(styleSheet.formFieldLabel)}>
									Set Minimum Donation Amount
								</label>

								<Controller
									name='hasMinimumDonationAmount'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Toggle
											id='has-minimum-donation-amount-toggle'
											isOn={fieldProps.value}
											onToggleCheckChanged={fieldProps.onChange}
											uncheckedColor={THEME.colors.background.dark}
										/>
									)}
								/>
							</div>

							<span className={css(styleSheet.formFieldDescription)}>
								Specify the minimum donation amount you would like to receive
							</span>
						</div>

						<div>
							<Controller
								name='minimumDonationAmount'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Input
										disabled={
											isArchived || !formValues.hasMinimumDonationAmount || !formValues.allowCustomDonationAmount
										}
										type='text'
										value={fieldProps.value ? `$${fieldProps.value}` : ''}
										onChange={e => {
											const inputValue = e.target.value;
											// Remove $ and any non-numeric characters except decimal point
											const numericValue = inputValue.replace(/[^0-9.]/g, '');
											const parsedValue = parseFloat(numericValue);

											if (numericValue === '' || numericValue === '.') {
												fieldProps.onChange(0);
											} else if (!isNaN(parsedValue)) {
												fieldProps.onChange(parsedValue);
											}
										}}
										placeholder='$0'
									/>
								)}
							/>

							{errors.minimumDonationAmount?.message && (
								<span className={css(styleSheet.errorMessage)}>{errors.minimumDonationAmount?.message}</span>
							)}
						</div>
					</div>
				</>
			)}

			<Separator />

			<div className={css(styleSheet.formField)}>
				<div className={css(styleSheet.formFieldLabelContainer)}>
					<div className={css(styleSheet.formFieldLabelWithToggleContainer)}>
						<label htmlFor={'allow-customer-to-cover-service-fees-toggle'} className={css(styleSheet.formFieldLabel)}>
							Ask Donors to cover processing fees
						</label>

						<Controller
							name='allowCustomerToCoverServiceFees'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Toggle
									id='allow-customer-to-cover-service-fees-toggle'
									isOn={fieldProps.value}
									onToggleCheckChanged={fieldProps.onChange}
									uncheckedColor={THEME.colors.background.dark}
								/>
							)}
						/>
					</div>

					<span className={css(styleSheet.formFieldDescription)}>
						Ask donors to cover the processing fee with a specific amount or percentage
					</span>
				</div>

				<div className={css(styleSheet.serviceFeeCalculationMethodContainer)}>
					<Controller
						name='serviceFeeCalculationMethod'
						control={control}
						render={({ field: { ref, ...fieldProps } }) => {
							return (
								<Select
									disabled={isArchived || !formValues.allowCustomerToCoverServiceFees}
									value={fieldProps.value}
									onValueChange={fieldProps.onChange}
								>
									<SelectTrigger>
										<SelectValue placeholder='Please select option' />
									</SelectTrigger>
									<SelectContent>
										{serviceFeeCalculationOptions.map(calculationOption => (
											<SelectItem key={calculationOption.value} value={calculationOption.value}>
												{calculationOption.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							);
						}}
					/>

					{formValues.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.FIXED && (
						<Controller
							name='serviceFeeFixedAmount'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input
									type='text'
									value={fieldProps.value ? `$${fieldProps.value}` : ''}
									onChange={e => {
										const inputValue = e.target.value;
										// Remove $ and any non-numeric characters except decimal point
										const numericValue = inputValue.replace(/[^0-9.]/g, '');
										const parsedValue = parseFloat(numericValue);

										if (numericValue === '' || numericValue === '.') {
											fieldProps.onChange(0);
										} else if (!isNaN(parsedValue)) {
											fieldProps.onChange(parsedValue);
										}
									}}
									placeholder='$0'
								/>
							)}
						/>
					)}

					{formValues.serviceFeeCalculationMethod === DonationServiceFeeCalculationMethod.PERCENTAGE && (
						<Controller
							name='serviceFeePercentage'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<Input
									type='text'
									value={fieldProps.value ? `${fieldProps.value}%` : ''}
									onChange={e => {
										const inputValue = e.target.value;
										// Remove % and any non-numeric characters except decimal point
										const numericValue = inputValue.replace(/[^0-9.]/g, '');
										const parsedValue = parseFloat(numericValue);

										if (numericValue === '' || numericValue === '.') {
											fieldProps.onChange(0);
										} else if (!isNaN(parsedValue)) {
											fieldProps.onChange(parsedValue);
										}
									}}
									placeholder='0%'
								/>
							)}
						/>
					)}
				</div>
			</div>
		</section>
	);
};
